import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { NavLink } from 'react-router-dom'
import { useAuth } from '../../../hooks/useAuth'
import { useGridFilters } from '../../../hooks/useGridFilters'
import { useVehicleOrderStats } from '../../../hooks/useOrderStats'
import { ICustomerViewModel } from '../../../types/interfaces'
import { LoadingSpinner } from '../../common/LoadingSpinner/LoadingSpinner'
import { OrderWarningCount } from '../../common/WarningCount/WarningCount'
import StatusCard from '../statusCard'
import './ContractStatus.scss'
import { ContractViewModel } from '@ail/contracts-api'

interface Props {
  treeViewMode: boolean
  contractsListLoading: boolean
  companyId?: string
  activeNaturesStats: {
    [id: string]: number
  }
  initializedNaturesStats: {
    [id: string]: number
  }
  expiringNaturesStats: {
    [id: string]: number
  }
  totalExpiringFilterWithinDays: string
  handleActiveRedirect: () => void
  handleInitializedRedirect: () => void
  handleExpiringRedirect: () => void
  contractsToShow: ContractViewModel[] | null
  handleExpirationDateChange: (e: React.ChangeEvent<HTMLSelectElement>) => void
  rootCompany?: ICustomerViewModel
  companies?: ICustomerViewModel[]
}

export function ContractStatus({
  handleActiveRedirect,
  handleInitializedRedirect,
  handleExpiringRedirect,
  treeViewMode,
  activeNaturesStats,
  initializedNaturesStats,
  expiringNaturesStats,
  totalExpiringFilterWithinDays,
  contractsListLoading,
  contractsToShow,
  companyId,
  handleExpirationDateChange,
  rootCompany,
  companies,
}: Props) {
  const { user } = useAuth()
  const { t } = useTranslation('common')
  const [updateFilterValue] = useGridFilters()
  const vehicleOrderStats = useVehicleOrderStats()

  const allowedIds = useMemo(() => {
    if (!rootCompany || !rootCompany.id) return []

    const children = rootCompany.children || []
    if (!children.length) return [rootCompany.id]

    const nestedChildren = children.filter((c) => c.children).flatMap((c) => c.children || [])
    const tree = [rootCompany, ...children, ...nestedChildren]

    const target = tree.find((c) => c.id === companyId)
    if (!target) return [rootCompany.id]

    const parentLength = target.parents?.length ?? 0
    if (parentLength === 2) return [target.id]

    if (parentLength === 1) {
      return target.children ? [rootCompany.id, target.id, ...target.children.map((c) => c.id)] : [target.id]
    }
    if (parentLength === 0) {
      const allChildIds = [...children.map((c) => c.id), ...nestedChildren.map((c) => c.id)]
      return children.length ? [target.id, ...allChildIds] : [target.id]
    }
    return [target.id]
  }, [companyId, rootCompany])

  const { stats, customersForRedirect } = useMemo(() => {
    if (!vehicleOrderStats) return { stats: undefined, customersForRedirect: undefined }
    const customersForRedirect: string[] = []
    const stats = { totalActionRequired: 0, totalFrozen: 0 }

    const source = rootCompany ? allowedIds : companies?.map((c) => c.id) || []
    if (!source || source.length === 0) return { stats: undefined, customersForRedirect }

    for (const id of source) {
      const stat = vehicleOrderStats[id]
      if (stat) {
        stats.totalActionRequired += stat.actionRequired || 0
        stats.totalFrozen += stat.frozen || 0
        customersForRedirect.push(`$${id.toUpperCase()}`)
      }
    }
    return { stats, customersForRedirect }
  }, [allowedIds, rootCompany, vehicleOrderStats, companies])
  const activeNaturesLength = Object.keys(activeNaturesStats).length
  const initializedNaturesLength = Object.keys(initializedNaturesStats).length
  const expiringNaturesLength = Object.keys(expiringNaturesStats).length
  const expirationOptions = [
    { value: 30, label: `30 ${t('DAYS')}` },
    { value: 90, label: `90 ${t('DAYS')}` },
    { value: 6 * 30, label: `6 ${t('MONTHS')}` },
    { value: 12 * 30, label: `12 ${t('MONTHS')}` },
    { value: 18 * 30, label: `18 ${t('MONTHS')}` },
    { value: 24 * 30, label: `24 ${t('MONTHS')}` },
    { value: 30 * 30, label: `30 ${t('MONTHS')}` },
    { value: 36 * 30, label: `36 ${t('MONTHS')}` },
    { value: 48 * 30, label: `48 ${t('MONTHS')}` },
  ]

  const totalStatCount = useMemo(() => {
    const expiring = Object.values(expiringNaturesStats).reduce((acc, stat) => acc + stat, 0)
    const active = Object.values(activeNaturesStats).reduce((acc, stat) => acc + stat, 0)
    const initialized = Object.values(initializedNaturesStats).reduce((acc, stat) => acc + stat, 0)

    return { expiring, active, initialized }
  }, [activeNaturesStats, expiringNaturesStats, initializedNaturesStats])
  const handleDeliveryRedirect = () => {
    if (!customersForRedirect) return
    const newFilters = { company: customersForRedirect, requestLink: true }
    updateFilterValue({ gridId: 'Delivery', newFilters })
  }
  const showStats = stats && (stats.totalActionRequired > 0 || stats.totalFrozen > 0)
  const hideStats = !companyId && (user?.isInternalUser ?? false) && !user?.isAdmin
  return (
    <div className="col-12 col-sm-12 col-lg-12 col-md-12 dashboardContractStatus">
      <div className={`box-title ${treeViewMode ? 'green-title' : ''}`}>
        <span>
          <i className="icon-contract"></i>
          {t('CONTRACT_STATUS')}
        </span>
      </div>
      <div className="box3 white">
        <div className="statusCardArea">
          <StatusCard
            type="active"
            activeItems={activeNaturesLength}
            data={activeNaturesStats}
            activeContractsAmount={totalStatCount.active}
            company={!!companyId}
            handleRedirect={handleActiveRedirect}
          />
          <StatusCard
            type="initialized"
            activeItems={initializedNaturesLength}
            data={initializedNaturesStats}
            initializedContractsAmount={totalStatCount.initialized}
            company={!!companyId}
            handleRedirect={handleInitializedRedirect}
            customChild={
              showStats && !hideStats ? (
                <NavLink
                  onClick={handleDeliveryRedirect}
                  to={'/orderAndDelivery'}
                  style={{ textDecoration: 'unset', color: '#fff', fontWeight: 'bold' }}
                >
                  <OrderWarningCount
                    showOrderWarningCount={true}
                    totalActionRequired={stats.totalActionRequired}
                    totalFrozenOrders={stats.totalFrozen}
                  />
                </NavLink>
              ) : undefined
            }
          />
          <StatusCard
            type="expiring"
            activeItems={expiringNaturesLength}
            data={expiringNaturesStats}
            expiringContractsAmount={totalStatCount.expiring}
            totalExpiringFilterWithinDays={totalExpiringFilterWithinDays}
            company={!!companyId}
            handleRedirect={handleExpiringRedirect}
          />
        </div>
        <div className="expContArea">
          <label>
            {!contractsListLoading
              ? !contractsToShow?.length
                ? totalExpiringFilterWithinDays &&
                  `${t('NO_EXPIRING_CONTRACTS_WITHIN')} ${(
                    parseInt(totalExpiringFilterWithinDays) / 30
                  ).toString()} ${t('MONTHS')}`
                : `${t('EXPIRING_CONTRACTS_LIST')}:`
              : null}
            <div className="selectArea">
              <select
                value={totalExpiringFilterWithinDays}
                onChange={handleExpirationDateChange}
                className="filterByExpiration"
              >
                {expirationOptions.map(({ value, label }) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
              <i className="icon-bottom" />
            </div>
          </label>
          {contractsListLoading ? (
            <div
              style={{
                display: 'flex',
                width: '100%',
                justifyContent: 'center',
                paddingLeft: '80px',
              }}
            >
              <LoadingSpinner />
            </div>
          ) : (
            !!contractsToShow?.length && (
              <ul className="expCont">
                {contractsToShow
                  .toSorted((a, b) => new Date(a.endDate ?? 0).getTime() - new Date(b.endDate ?? 0).getTime())
                  .slice(0, 6)
                  .map((obj) => (
                    <li key={obj.id}>
                      <div className="flex">
                        <NavLink to={`/generalContracts/details/${obj.id}`}>{obj.reference}</NavLink>
                        <div className="driver">{obj.vehicle?.driver?.displayName}</div>
                      </div>
                      <div className="expDate">
                        <div className="group">
                          {Math.round(
                            (new Date(obj.endDate ?? 0).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24) + 1
                          )}{' '}
                          Days left
                        </div>
                        <NavLink to={`/generalContracts/details/${obj.id}`}>
                          <i className="icon-right" />
                        </NavLink>
                      </div>
                    </li>
                  ))}
              </ul>
            )
          )}
          {totalStatCount.expiring > 0 &&
            !contractsListLoading &&
            ((!companyId && !user?.isInternalUser) || !!companyId) && (
              <div className="btnArea">
                <a className="btnSmall" onClick={handleExpiringRedirect}>
                  {t('SHOW_ALL')}
                </a>
              </div>
            )}
        </div>
      </div>
    </div>
  )
}
